<template>
  <div
    style="
      display: flex;
      justify-content: start;
      align-items: center;
      flex-direction: column;
      width: 100%;
    ">
    <!-- Edit Trigger Dialog -->
    <v-dialog
      max-width="500"
      v-model="editDialog">
      <v-card
        v-if="editingTrigger"
        class="dialog-card">
        <ModalHeader
          icon="mdi-pencil"
          modalTitle="Edit Trigger"
          @close="editDialog = false" />
        <div class="pa-4">
          <div
            class="mb-3"
            style="font-size: 16px; font-weight: 500">
            {{ FormatType(editingTrigger) }}
          </div>
          <TriggerVars :vars="editingTrigger.vars" />
        </div>
        <div class="modal-footer">
          <v-spacer />
          <v-btn
            variant="flat"
            block
            color="primary"
            @click="editDialog = false">
            Done
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-card
      v-for="(trigger, index) in item.triggers"
      :key="index"
      style="
        background: var(--v-navBarBackground-base);
        border-radius: 4px;
        overflow-y: hidden;
        display: flex;
        justify-content: start;
        align-items: center;
        align-content: center;
        max-width: 1000px;
        width: 100%;
        height: unset !important;
      "
      class="mb-3"
      flat>
      <!-- Only show thumbnail for scene-based triggers -->
      <v-img
        v-if="hasSceneSelectVar(trigger)"
        style="background: #2d2d2d; border-radius: 5px; flex-grow: 0"
        height="100"
        width="180"
        muted
        loop
        loading="lazy"
        :src="state.devRedirect + state.$ident + FindFirstAsset(trigger)" />
      <v-card
        style="
          background: var(--v-navBarBackground-base);
          flex-grow: 1;
          height: unset !important;
        "
        flat>
        <v-card-text>
          <div style="display: flex; font-size: 18px; align-items: center">
            <v-icon class="mr-3 mb-0">{{ triggerIcon(trigger) }}</v-icon>
            {{ FormatType(trigger) }}
            <v-spacer />
            <v-btn
              dark
              @click="openEditDialog(trigger)"
              size="small"
              variant="flat"
              class="mr-2"
              style="background-color: var(--inner-background)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              dark
              @click="DeleteTrigger(trigger.uid)"
              size="small"
              variant="flat"
              class="mr-2"
              style="background-color: var(--inner-background)">
              <v-icon>mdi-trash-can</v-icon>
            </v-btn>
          </div>
          <div
            class="mb-2"
            style="opacity: 0.6">
            {{ GetTriggerSubtitle(trigger) }}
          </div>
          <div style="display: flex">
            <Tag
              v-for="frame in get_ch_frames(trigger)"
              :key="frame"
              dense
              :name="frame.nickname ? frame.nickname : 'Frame ' + frame.number"
              :color="frame.color"
              :icon="`mdi-numeric-${frame.number}-box`" />
          </div>
        </v-card-text>
      </v-card>
    </v-card>
  </div>
</template>
<script setup>
import { defineProps, ref } from 'vue';
import { state } from '../../stores/deluge.js';
import { deluge } from '@/stores/deluge.js';

import { $http } from '../../stores/http.js';
import Tag from './Tag.vue';
import TriggerVars from './TriggerVars.vue';
import ModalHeader from './Modals/ModalHeader.vue';
import { getThumbnailForScene } from '@/utils/thumbnails.js';

const props = defineProps(['item']);

const editDialog = ref(false);
const editingTrigger = ref(null);

function openEditDialog(trigger) {
  editingTrigger.value = trigger;
  editDialog.value = true;
}

function triggerIcon(trigger) {
  const iconMap = {
    start_scene: 'mdi-access-point',
    start_preset: 'mdi-palette-swatch',
    start_asset: 'mdi-image',
    start_name_match: 'mdi-form-textbox',
    http_req: 'mdi-web',
    osc_send: 'mdi-sine-wave',
    start_serial_command: 'mdi-serial-port',
    start_scene_data: 'mdi-code-json',
  };
  return iconMap[trigger.type] || 'mdi-flash';
}

function hasSceneSelectVar(trigger) {
  return trigger.vars?.some((v) => v.type === 'scene_select');
}
// thumbnails are obtained via getThumbnailForScene when needed
async function DeleteTrigger(uid) {
  props.item.triggers = props.item.triggers.filter(
    (trigger) => trigger.uid !== uid
  );
}
function get_ch_frames(trigger) {
  let frames = [];
  var dynamic_item = undefined;
  try {
    for (const trigger_var of trigger.vars) {
      if (trigger_var.type === 'scene_select') {
        for (const item of Object.values(deluge.items)) {
          if (item.scene_name) {
            if (item.id === trigger_var.value.value) {
              dynamic_item = item;
            }
          }
        }
      }
    }
    if (dynamic_item) {
      for (const frame of Object.values(deluge.frames)) {
        if (frame.channel_id === deluge.items[dynamic_item.ch_did].id) {
          frames.push(frame);
        }
      }
    }
  } catch {
    return [];
  }

  return frames;
}
function FormatType(trigger) {
  const typeMap = {
    start_scene: 'Start Scene',
    start_preset: 'Start Preset',
    start_asset: 'Start Asset',
    start_name_match: 'Name Match',
    http_req: 'HTTP Request',
    osc_send: 'OSC Send',
    start_serial_command: 'Serial Command',
    start_scene_data: 'Scene Data',
  };
  return typeMap[trigger.type] || trigger.type;
}
function GetTriggerSubtitle(trigger) {
  // Handle scene_select type (for start_scene trigger)
  for (const trigger_var of trigger.vars) {
    if (trigger_var.type === 'scene_select') {
      for (const item of Object.values(deluge.items)) {
        if (item.scene_name) {
          if (item.id === trigger_var.value?.value) {
            return item.name;
          }
        }
      }
      // Scene select but scene not found
      return 'Scene not found, please remove trigger.';
    }
  }

  // Handle specific trigger types
  switch (trigger.type) {
    case 'http_req': {
      const urlVar = trigger.vars.find((v) => v.name === 'URL');
      const methodVar = trigger.vars.find((v) => v.name === 'Method');
      if (urlVar?.value) {
        return `${methodVar?.value || 'GET'} ${urlVar.value}`;
      }
      return 'HTTP Request';
    }
    case 'osc_send': {
      const ipVar = trigger.vars.find((v) => v.name === 'IP');
      const portVar = trigger.vars.find((v) => v.name === 'Port');
      const pathVar = trigger.vars.find((v) => v.name === 'Path');
      return `${ipVar?.value || '127.0.0.1'}:${portVar?.value || '9000'}${pathVar?.value || '/'}`;
    }
    case 'start_preset': {
      const presetVar = trigger.vars.find((v) => v.type === 'preset_select');
      return presetVar?.value?.name || 'Preset';
    }
    case 'start_asset': {
      const assetVar = trigger.vars.find((v) => v.type === 'asset_select');
      return assetVar?.value?.name || 'Asset';
    }
    case 'start_name_match': {
      const matchVar = trigger.vars.find((v) => v.name === 'Match Name');
      return matchVar?.value ? `Match: "${matchVar.value}"` : 'Name Match';
    }
    case 'start_serial_command': {
      const portVar = trigger.vars.find((v) => v.name === 'Serial Port');
      const cmdVar = trigger.vars.find((v) => v.name === 'Write Command');
      return `${portVar?.value || 'COM?'}: ${cmdVar?.value || '(no command)'}`;
    }
    case 'start_scene_data': {
      const dataVar = trigger.vars.find((v) => v.name === 'SceneData');
      return dataVar?.value ? 'Scene Data Trigger' : 'Scene Data';
    }
    default:
      return trigger.name || 'Trigger';
  }
}
function FindFirstAsset(trigger) {
  for (const trigger_var of trigger.vars) {
    if (trigger_var.type === 'scene_select') {
      for (const item of Object.values(deluge.items)) {
        if (item.scene_name) {
          if (item.id === trigger_var.value.value) {
            const r = getThumbnailForScene(item, deluge);
            return r.src;
          }
        }
      }
    }
  }
}
function triggersAndMetadata(did) {
  var temp = deluge.templates[deluge.items[did].scene_name];
  if (temp.default_triggers) {
    for (var i = 0; i < temp.default_triggers.length; i++) {
      const name = temp.default_triggers[i].type;
      temp.default_triggers[i] = {
        ...deluge.triggers[name],
        ...temp.default_triggers[i],
      };
    }
  }

  return temp.default_triggers;
}
</script>
